import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function StakeholderManager() {
  const navigate = useNavigate();
  const [stakeholders, setStakeholders] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, eRes] = await Promise.all([
        api.get("/stakeholders"),
        api.get("/email-logs"),
      ]);
      setStakeholders(sRes.data);
      setEmailLogs(eRes.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addStakeholder = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/stakeholders", { name, email });
      setName("");
      setEmail("");
      fetchData();
      toast.success("Stakeholder added");
    } catch {
      toast.error("Failed to add stakeholder");
    } finally {
      setSubmitting(false);
    }
  };

  const removeStakeholder = async (id) => {
    try {
      await api.delete(`/stakeholders/${id}`);
      fetchData();
      toast.success("Stakeholder removed");
    } catch {
      toast.error("Failed to remove stakeholder");
    }
  };

  return (
    <div className="space-y-8 max-w-4xl fade-in" data-testid="stakeholder-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Stakeholders
        </h1>
        <p className="text-[#525C58] mt-1">Manage notification recipients for approved visitor requests</p>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Stakeholder</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end flex-wrap" data-testid="add-stakeholder-form">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label className="text-[#1C1F1E]">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Stakeholder name"
                data-testid="stakeholder-name-input"
              />
            </div>
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label className="text-[#1C1F1E]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@kapsowar.org"
                data-testid="stakeholder-email-input"
              />
            </div>
            <Button
              onClick={addStakeholder}
              className="bg-[#1B4332] hover:bg-[#2D6A4F] transition-colors duration-200"
              disabled={submitting}
              data-testid="add-stakeholder-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Current Stakeholders ({stakeholders.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="stakeholder-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[#7A8A84] font-medium">Name</TableHead>
                <TableHead className="text-[#7A8A84] font-medium">Email</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {stakeholders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-[#7A8A84] py-8">
                    No stakeholders added yet
                  </TableCell>
                </TableRow>
              ) : (
                stakeholders.map((s) => (
                  <TableRow key={s.id} data-testid={`stakeholder-row-${s.id}`}>
                    <TableCell className="font-medium text-[#1C1F1E]">{s.name}</TableCell>
                    <TableCell className="text-[#525C58]">{s.email}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStakeholder(s.id)}
                        className="h-8 w-8 p-0 hover:bg-red-50"
                        data-testid={`delete-stakeholder-${s.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="h-5 w-5 text-[#40916C]" /> Email Logs (MOCKED)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {emailLogs.length === 0 ? (
            <p className="text-[#7A8A84] text-sm py-4" data-testid="no-email-logs">
              No notification emails sent yet. Emails are sent when a visitor request completes all approvals.
            </p>
          ) : (
            <div className="space-y-3" data-testid="email-logs-list">
              {emailLogs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]"
                  data-testid={`email-log-${log.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm text-[#1C1F1E]">{log.subject}</p>
                      <p className="text-xs text-[#525C58] mt-1">To: {log.recipients.join(", ")}</p>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                      {log.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#7A8A84] mt-2">{new Date(log.sent_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
